/**
 * OSP Index API constants (headers, environment, base URLs).
 *
 * @source @open-social-protocol/osp-api-index-types
 *   - src/constant.ts
 *   - src/environments.ts
 */

import log from "logbeacon";

/** OSP Index API request header names */
export const HEADERS = {
  APP_ID: "OS-App-Id",
  CHAIN_ID: "OS-Chain-Id",
  GUEST_ID_MARKETING: "OS-Guest-Id-Marketing",
  /** 域名/App 升级后的品牌标识，与旧 OSP 产品区分 */
  REG_BRAND: "OS-Reg-Brand",
} as const;

/** `OS-Reg-Brand` 固定值（全端一致） */
export const OSP_REG_BRAND = "edwealth";

/** ospprotocol Index 后端请求应带的品牌头（Chat/perplexica 不要带 —— CORS 未放行） */
export function ospRegBrandHeaders(): Record<string, string> {
  return { [HEADERS.REG_BRAND]: OSP_REG_BRAND };
}

export enum Environment {
  local = "local",
  dev = "dev",
  alpha = "alpha",
  beta = "beta",
  pre = "pre",
  prod = "prod",
}

/** Index API root per env (trailing path is appended by callers, e.g. `/profiles/...`) */
export const EnvIndexUrl: { [name: string]: string } = {
  local: "https://api.dev.ospprotocol.xyz/v2",
  dev: "https://api.dev.ospprotocol.xyz/v2",
  alpha: "https://api.alpha.ospprotocol.xyz/v2",
  beta: "https://api.beta.ospprotocol.xyz/v2",
  pre: "https://api.opensocial.fun/v2",
  prod: "https://api.edwealth.ai/v2",
};

/** Fallback when `NEXT_PUBLIC_APP_ID` is unset (matches SSR helper historical `|| "100"`) */
export const DEFAULT_OSP_APP_ID = "100";

/** SDK `TOKEN_EXP_TIME` — id_token cache buffer before re-fetching (30 minutes). */
export const ID_TOKEN_CACHE_BUFFER_MS = 1800 * 1000;

export function getEnvIndexStorageBaseUrl(
  environment: Environment | string,
): string {
  return EnvIndexUrl[environment] ?? EnvIndexUrl[Environment.dev];
}

/** Index API root 的版本前缀：EnvIndexUrl 各环境与 opensocial 默认 base-path 均为 /v2 */
const INDEX_API_VERSION_PATH = "/v2";

/** 本函数被每个 API client 调用，覆盖值有误时只提示一次 */
let hasWarnedOverrideWithoutVersion = false;

/**
 * 本地覆盖值缺 /v2 时提示：请求会打到网关上不存在的路由，预检 404 被浏览器当作 CORS 拦截，
 * 控制台只剩 `TypeError: Failed to fetch`（登录最先暴露），很难从报错反推到这里。
 */
function warnIfOverrideWithoutVersion(override: string): void {
  if (
    hasWarnedOverrideWithoutVersion ||
    override.endsWith(INDEX_API_VERSION_PATH)
  ) {
    return;
  }
  hasWarnedOverrideWithoutVersion = true;
  const hint =
    `NEXT_PUBLIC_OSP_API_HOST="${override}" 未以 ${INDEX_API_VERSION_PATH} 结尾。` +
    `接口会打到网关上不存在的路由，CORS 预检返回 404，浏览器里登录等请求报 "TypeError: Failed to fetch"。` +
    `请在 .env / .env.local 中改为 "${override}${INDEX_API_VERSION_PATH}" 后重启 dev；` +
    `若无需连接自定义后端，删除该变量即可按 NEXT_PUBLIC_ENV 走默认地址。`;
  log.warn(`[OSP API] ${hint}`);
}

/**
 * Index API root for the given OSP environment (same mapping as SDK `AuthModule` → `indexClient.baseUrl`).
 * When NEXT_PUBLIC_OSP_API_HOST is set it wins (local dev against a locally-run opensocial).
 */
export function getEnvIndexBaseUrl(environment: Environment | string): string {
  // override 仅本地开发构建生效（EDG-529 本地连本地栈用）。线上构建一律走 EnvIndexUrl 表：
  // beta CI Variables 里存在历史遗留的裸 host 值，被 override 读取后导致整族 client
  // baseUrl 缺 /v2（打不到网关路由，预检 404 表现为 CORS 拦，2026-09-11 beta 实测）。
  // 本地 .env 填裸 host 同样会踩到，故缺 /v2 时在控制台提示（不改写取值，避免掩盖配置问题）。
  if (process.env.NODE_ENV === "development") {
    const override = process.env.NEXT_PUBLIC_OSP_API_HOST;
    if (override) {
      const baseUrl = override.replace(/\/+$/, "");
      warnIfOverrideWithoutVersion(baseUrl);
      return baseUrl;
    }
  }
  return getEnvIndexStorageBaseUrl(environment);
}
